import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, Trash2, Loader2, Plus, Lock } from "lucide-react";
import { J, MANROPE, ddMon } from "./journeyTokens";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customer-success`;
const LABEL = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", color: J.ink2 };
const INPUT = "h-9 rounded-[7px] px-2.5 text-[13px] bg-white";

const ddmon = (iso) => (iso ? ddMon(iso) : "—");

/* Implementation hours in full: what this client is allowed, what has been
   spent, and every entry that makes up the total so a wrong one can go.
   4 and 12 are only defaults — the allowance is per journey, and 0 means no cap. */
export const JourneyHoursDialog = ({ clientId, companyName, onClose, onSaved, onError }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [hours, setHours] = useState("");
  const [note, setNote] = useState("");
  const [when, setWhen] = useState(new Date().toISOString().slice(0, 10));
  const [allowance, setAllowance] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/journeys/${clientId}/hours`);
      setD(r.data);
      setAllowance(r.data.is_override ? String(r.data.allowance) : "");
    } catch (e) { onError(e?.response?.data?.detail || "Could not read those hours"); }
  }, [clientId, onError]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const v = parseFloat(hours);
    if (!v) return;
    setBusy("add");
    try {
      await axios.post(`${API}/journeys/${clientId}/hours`,
        { hours: v, note: note.trim() || null, entry_date: when });
      setHours(""); setNote("");
      await load(); await onSaved();
    } catch (e) { onError(e?.response?.data?.detail || "Could not log those hours"); }
    finally { setBusy(""); }
  };

  const remove = async (entry) => {
    setBusy(entry.id);
    try {
      await axios.delete(`${API}/journeys/hours/${entry.id}`);
      await load(); await onSaved();
    } catch (e) { onError(e?.response?.data?.detail || "Could not remove that entry"); }
    finally { setBusy(""); }
  };

  const saveAllowance = async (reset) => {
    setBusy("allowance");
    try {
      await axios.post(`${API}/journeys/${clientId}/hours-allowance`,
        reset ? { reset: true } : { hours: allowance === "" ? null : Number(allowance) });
      await load(); await onSaved();
    } catch (e) { onError(e?.response?.data?.detail || "Could not set the hours"); }
    finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 z-[80]" data-testid="journey-hours-dialog">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.38)" }} onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col rounded-[11px] overflow-hidden"
        style={{ width: "min(560px, 94vw)", maxHeight: "88vh", background: "#fff", border: `1px solid ${J.rule}` }}>

        <div className="px-5 py-3.5 flex items-start justify-between gap-3"
          style={{ background: J.head, borderBottom: `1px solid ${J.rule}` }}>
          <div className="min-w-0">
            <div style={{ ...MANROPE, fontSize: 17, fontWeight: 800, color: J.ink }}>Implementation hours</div>
            <div className="mono text-[11px] mt-0.5 truncate" style={{ color: J.muted }}>{companyName}</div>
          </div>
          <button onClick={onClose} data-testid="journey-hours-close"
            className="w-8 h-8 rounded-[7px] flex items-center justify-center hover:bg-[#F5F2EA]"
            style={{ color: J.ink2 }}><X className="w-4 h-4" /></button>
        </div>

        {!d ? (
          <div className="p-6 flex items-center gap-2 text-[13px]" style={{ color: J.muted }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Reading the hours…
          </div>
        ) : (
          <div className="overflow-y-auto px-5 py-4 space-y-4">
            <div className="rounded-[9px] p-3" style={{ background: J.head, border: `1px solid ${J.edge}` }}>
              <div className="flex items-baseline gap-2">
                <span className="mono" data-testid="journey-hours-total"
                  style={{ fontSize: 22, fontWeight: 700, color: d.over ? J.overdue : J.ink }}>
                  {d.total}h
                </span>
                <span className="text-[13px]" style={{ color: J.ink2 }}>
                  {d.capped ? `of ${d.allowance}h` : "logged · no cap set"}
                </span>
                {d.capped && !d.over && (
                  <span className="text-[12px] ml-auto" style={{ color: J.muted }}>
                    {d.remaining}h left
                  </span>
                )}
                {d.over && (
                  <span className="text-[12px] font-bold ml-auto" style={{ color: J.overdue }}
                    data-testid="journey-hours-over">
                    {Math.round((d.total - d.allowance) * 100) / 100}h over
                  </span>
                )}
              </div>
            </div>

            <div>
              <div style={LABEL}>HOURS THIS CLIENT GETS</div>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <input type="number" min="0" step="0.25" value={allowance}
                  data-testid="journey-hours-allowance-input"
                  onChange={(e) => setAllowance(e.target.value)}
                  placeholder={`${d.track_default} (track default)`}
                  className="h-9 w-[150px] rounded-[7px] px-2 text-[13px] bg-white"
                  style={{ border: `1px solid ${J.edge}` }} />
                <button onClick={() => saveAllowance(false)} disabled={busy === "allowance"}
                  data-testid="journey-hours-allowance-save"
                  className="h-9 px-3 rounded-[7px] text-[12px] font-bold disabled:opacity-60"
                  style={{ background: J.accent, border: `1px solid ${J.accentEdge}`, color: J.accentInk }}>
                  {busy === "allowance" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Set"}
                </button>
                {d.is_override && (
                  <button onClick={() => saveAllowance(true)} disabled={busy === "allowance"}
                    data-testid="journey-hours-allowance-reset"
                    className="h-9 px-2 rounded-[7px] text-[12px] font-semibold"
                    style={{ color: J.ink2, border: `1px solid ${J.edge}`, background: "#fff" }}>
                    Back to {d.track_default}h
                  </button>
                )}
              </div>
              <div className="text-[11.5px] mt-1" style={{ color: J.muted }}>
                {d.is_override
                  ? `Set for this client. The track default is ${d.track_default}h.`
                  : `Using the ${d.track_default}h track default — change it here if this client agreed something else.`}
                {" "}Enter <span className="mono">0</span> for no cap, or leave blank to use the default.
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${J.rule}` }} className="pt-3">
              <div style={LABEL}>LOG TIME</div>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <input type="number" step="0.25" value={hours} onChange={(e) => setHours(e.target.value)}
                  placeholder="Hours" data-testid="journey-hours-add-hours"
                  className="h-9 w-[86px] rounded-[7px] px-2 text-[13px] bg-white"
                  style={{ border: `1px solid ${J.edge}` }} />
                <input type="date" value={when} onChange={(e) => setWhen(e.target.value)}
                  data-testid="journey-hours-add-date" className={INPUT}
                  style={{ border: `1px solid ${J.edge}`, width: 150 }} />
                <input value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="What was it for?" data-testid="journey-hours-add-note"
                  className="h-9 flex-1 min-w-[140px] rounded-[7px] px-2 text-[13px] bg-white"
                  style={{ border: `1px solid ${J.edge}` }} />
                <button onClick={add} disabled={!parseFloat(hours) || busy === "add"}
                  data-testid="journey-hours-add"
                  className="h-9 px-3 rounded-[7px] text-[12px] font-bold inline-flex items-center gap-1 disabled:opacity-60"
                  style={{ background: parseFloat(hours) ? J.accent : "#E6E2D9",
                           border: `1px solid ${J.accentEdge}`, color: J.accentInk }}>
                  {busy === "add" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  Add
                </button>
              </div>
              <div className="text-[11.5px] mt-1" style={{ color: J.muted }}>
                A negative number takes hours off, if that is easier than deleting an entry.
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${J.rule}` }} className="pt-3">
              <div style={LABEL}>WHERE THE {d.total}H CAME FROM</div>
              {d.entries.length === 0 ? (
                <div className="text-[13px] mt-1.5" style={{ color: J.muted }}>
                  Nothing logged yet.
                </div>
              ) : d.entries.map((e) => (
                <div key={e.id} data-testid={`journey-hours-entry-${e.id}`}
                  className="flex items-center gap-2 py-1.5"
                  style={{ borderBottom: `1px solid ${J.rule}` }}>
                  <span className="mono text-[13px] w-[52px] shrink-0 text-right"
                    style={{ color: e.hours < 0 ? J.overdue : J.ink }}>{e.hours}h</span>
                  <span className="text-[12px] w-[58px] shrink-0" style={{ color: J.muted }}>
                    {ddmon(e.entry_date)}
                  </span>
                  <span className="text-[12.5px] flex-1 min-w-0 truncate" style={{ color: J.ink2 }}>
                    {e.note || "No note"}
                    {e.user_name ? <span style={{ color: J.muted }}> · {e.user_name}</span> : null}
                  </span>
                  {e.source === "action" ? (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase"
                      style={{ color: J.muted }} title="Logged by completing a step — reopen the step to remove it">
                      <Lock className="w-3 h-3" /> step
                    </span>
                  ) : (
                    <button onClick={() => remove(e)} disabled={busy === e.id}
                      data-testid={`journey-hours-delete-${e.id}`} title="Remove this entry"
                      className="shrink-0 p-1 rounded hover:bg-[#F5F2EA] disabled:opacity-50">
                      {busy === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                     : <Trash2 className="w-3.5 h-3.5" style={{ color: J.ink2 }} />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
